import React, { useRef, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Alert, IconButton } from '@mui/material';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import apiClient from '../../lib/axios';

interface Props {
  /** Display label, e.g. "Cédula" */
  label: string;
  /** Sub-line under the label */
  description: string;
  /** Pre-existing URL (if any) — switches button copy to "Reemplazar" and shows a "Ver archivo" link */
  initialUrl?: string;
  /** Called after a successful upload with the new URL */
  onUploaded?: (url: string) => void;
  /** Called after a successful delete; if omitted, the delete button is hidden */
  onDelete?: () => Promise<void>;

  /* === One of these two upload modes is required === */
  /** Custom upload function — receives the chosen File, returns the persisted URL */
  uploadFn?: (file: File) => Promise<{ url: string }>;
  /** Tracking-flow upload to the authenticated /public-orders/track/:trackingUUID/documents */
  trackingUUID?: string;
  type?: 'CEDULA' | 'INSURANCE_CARD';
}

export const DocumentUploadZone: React.FC<Props> = ({
  label,
  description,
  initialUrl,
  onUploaded,
  onDelete,
  uploadFn,
  trackingUUID,
  type,
}) => {
  const [currentUrl, setCurrentUrl] = useState(initialUrl);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      let url: string;
      if (uploadFn) {
        const res = await uploadFn(file);
        url = res.url;
      } else if (trackingUUID && type) {
        const form = new FormData();
        form.append('type', type);
        form.append('file', file);
        const { data } = await apiClient.post(`/public-orders/track/${trackingUUID}/documents`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        url = data.url;
      } else {
        throw new Error('DocumentUploadZone needs either uploadFn or trackingUUID+type');
      }
      setCurrentUrl(url);
      onUploaded?.(url);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await onDelete();
      setCurrentUrl(undefined);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Error al eliminar el archivo');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        border: '1px dashed',
        borderColor: currentUrl ? 'success.light' : 'divider',
        bgcolor: currentUrl ? 'rgba(0,168,107,0.04)' : 'transparent',
        borderRadius: 2,
        p: 2,
        mb: 2,
        transition: 'border-color 160ms ease-out, background-color 160ms ease-out',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, color: 'text.headline', display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {label}
            {currentUrl && <CheckCircleRoundedIcon sx={{ fontSize: 16, color: 'success.main' }} />}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {description}
          </Typography>
        </Box>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            // reset so picking the same file twice still fires onChange
            e.target.value = '';
          }}
        />
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Button
            variant={currentUrl ? 'outlined' : 'contained'}
            size="small"
            startIcon={
              uploading ? <CircularProgress size={14} color="inherit" /> : <CloudUploadRoundedIcon />
            }
            onClick={() => inputRef.current?.click()}
            disabled={uploading || deleting}
            sx={{ borderRadius: 999, minHeight: 36, px: 1.75 }}
          >
            {currentUrl ? 'Reemplazar' : 'Subir'}
          </Button>
          {currentUrl && onDelete && (
            <IconButton
              size="small"
              aria-label={`Eliminar ${label}`}
              onClick={handleDelete}
              disabled={uploading || deleting}
              sx={{ color: 'error.main' }}
            >
              {deleting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          )}
        </Box>
      </Box>
      {currentUrl && (
        <Box sx={{ mt: 1 }}>
          <Typography
            component="a"
            href={currentUrl}
            target="_blank"
            rel="noreferrer"
            sx={{
              fontSize: 12,
              color: 'primary.dark',
              textDecoration: 'underline',
              '&:hover': { color: 'primary.main' },
            }}
          >
            Ver archivo
          </Typography>
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};
